import React from 'react';
import { Dialog, DialogTitle, DialogContent } from '@mui/material';
import Parse from 'parse';
import TiptapCanvas from './TiptapCanvas';
import StudioAssistant from './StudioAssistant';
import { templateContent, templateLabel } from './templateContent';
import { studioTemplates, studioBlocks } from './studioTemplates';
import { lifeTaggerApi } from '../api/lifetaggerApi';
import { editableHtml, escapeHtml, replaceRichContent } from './richContent';
import { editorDocument } from './composerDocument';
import './LifeObjectComposer.css';

const clean = templateContent;

export default function LifeObjectComposer({
  content,
  onChange,
  onBusy,
  title,
}) {
  const rich = content
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => ['html', 'text'].includes(item.type));
  const [choice, setChoice] = React.useState(null);
  const index = rich.some((entry) => entry.index === choice)
    ? choice
    : (rich.find(({ item }) => item.type === 'html') || rich[0])?.index ?? -1;
  const value = editableHtml(content[index]);
  const editor = React.useRef(null);
  const [mode, setMode] = React.useState('write');
  const [width, setWidth] = React.useState('wide');
  const [panel, setPanel] = React.useState(null);
  const [focus, setFocus] = React.useState(false);
  const [startedWriting, setStartedWriting] = React.useState(false);
  const [previous, setPrevious] = React.useState(null);
  React.useEffect(() => {
    setPrevious(null);
  }, [index]);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const onEditorReady = React.useCallback((instance) => {
    editor.current = instance;
  }, []);
  const [templates, setTemplates] = React.useState(studioTemplates);
  const [templateCollection, setTemplateCollection] = React.useState('studio');
  const [templateSearch, setTemplateSearch] = React.useState('');
  const [templateCategory, setTemplateCategory] = React.useState('All');
  const visibleTemplates = templates.filter(
    (template) =>
      (templateCollection !== 'studio' ||
        templateCategory === 'All' ||
        template.category === templateCategory) &&
      `${template.name || ''} ${template.title || ''} ${
        template.description || ''
      } ${template.category || ''}`
        .toLowerCase()
        .includes(templateSearch.toLowerCase())
  );
  const [templatesLoading, setTemplatesLoading] = React.useState(false);
  const [templatesError, setTemplatesError] = React.useState(false);
  const [templateRetry, setTemplateRetry] = React.useState(0);
  const [source, setSource] = React.useState('');
  const change = (html, json) =>
    onChange(
      replaceRichContent(content, index, html, editorDocument(html, json))
    );
  const insert = (html) => {
    if (editor.current && mode === 'write')
      editor.current.insertContent(clean(html));
    else change(value + clean(html));
  };
  React.useEffect(() => {
    onBusy?.(busy);
  }, [busy, onBusy]);
  React.useEffect(() => {
    if (panel !== 'templates') return undefined;
    if (templateCollection === 'studio') {
      setTemplates(studioTemplates);
      setTemplatesLoading(false);
      setTemplatesError(false);
      return undefined;
    }
    let active = true;
    setTemplatesLoading(true);
    setTemplatesError(false);
    Parse.Cloud.run('getTagTemplates')
      .then(
        (items) =>
          active &&
          setTemplates(
            Array.isArray(items)
              ? items.map((item) => ({
                  ...item,
                  name: templateLabel(item.name),
                  title: templateLabel(item.title),
                  description: templateLabel(item.description),
                  html: templateContent(item.html),
                }))
              : []
          )
      )
      .catch(() => active && setTemplatesError(true))
      .finally(() => active && setTemplatesLoading(false));
    return () => {
      active = false;
    };
  }, [panel, templateRetry, templateCollection]);
  const upload = async (file) => {
    const asset = await lifeTaggerApi.uploadAsset(file);
    if (!asset.url)
      throw new Error('The upload did not return a usable media address.');
    return asset.url;
  };
  const attach = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const url = escapeHtml(await upload(file));
      const title = escapeHtml(file.name);
      insert(
        file.type.startsWith('image/')
          ? `<p><img src="${url}" alt="${title}" style="max-width:100%;height:auto;border-radius:16px"></p>`
          : file.type.startsWith('video/')
          ? `<p><video controls src="${url}" style="max-width:100%"></video></p>`
          : file.type.startsWith('audio/')
          ? `<p><audio controls src="${url}"></audio></p>`
          : `<p><a href="${url}" target="_blank" rel="noopener noreferrer">${title}</a></p>`
      );
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className={`lt-composer lt-studio-v2 ${
        panel && panel !== 'templates' ? 'lt-panel-open' : ''
      } ${focus ? 'lt-composer-focused' : ''}`}
    >
      <header className="lt-composer-heading">
        <div>
          <span>THE EXPERIENCE</span>
          <h2>Make something worth discovering.</h2>
        </div>
        <p>Your words. Your world. Your way.</p>
      </header>
      <div className="lt-composer-bar">
        <button onClick={() => setFocus(!focus)} aria-pressed={focus}>
          {focus ? '↙ Exit focus' : '↗ Focus'}
        </button>
        {previous !== null && (
          <button
            onClick={() => {
              const current = value;
              change(previous);
              setPrevious(current);
            }}
          >
            Restore previous version
          </button>
        )}
        <div role="group" aria-label="Editor mode">
          {['write', 'preview', 'html'].map((tab) => (
            <button
              key={tab}
              aria-pressed={mode === tab}
              onClick={() => {
                if (tab === 'html') setSource(value);
                setMode(tab);
              }}
            >
              {tab === 'write'
                ? 'Compose'
                : tab === 'html'
                ? 'HTML'
                : 'Preview'}
            </button>
          ))}
        </div>
        {rich.length > 1 && (
          <select
            aria-label="Content section"
            value={index}
            onChange={(e) => {
              setChoice(Number(e.target.value));
              setMode('write');
            }}
          >
            {rich.map(({ item, index: i }, order) => (
              <option key={i} value={i}>
                {item.label || `Content section ${order + 1}`}
              </option>
            ))}
          </select>
        )}
        <div role="group" aria-label="Canvas width">
          {['wide', 'phone'].map((size) => (
            <button
              key={size}
              aria-pressed={width === size}
              onClick={() => setWidth(size)}
            >
              {size === 'wide' ? 'Desktop' : 'Phone'}
            </button>
          ))}
        </div>
      </div>
      {error && (
        <p className="lt-composer-error" role="alert">
          {error}
        </p>
      )}
      <div className="lt-composer-workspace">
        <nav className="lt-tool-rail" aria-label="Creative tools">
          {[
            ['layouts', '▧', 'Sections'],
            ['media', '＋', 'Media'],
            ['assistant', '✧', 'Create'],
            ['templates', '▤', 'Templates'],
          ].map(([id, icon, label]) => (
            <button
              key={id}
              aria-pressed={panel === id}
              onClick={() => setPanel(panel === id ? null : id)}
            >
              <span aria-hidden="true">{icon}</span>
              {label}
            </button>
          ))}
        </nav>
        <aside hidden={!panel || panel === 'templates'}>
          <div className="lt-panel-heading">
            <strong>
              {
                {
                  layouts: 'Build your story',
                  media: 'Bring your story to life',
                  assistant: 'A creative partner',
                  templates: 'The template library',
                }[panel]
              }
            </strong>
            <button
              aria-label="Close creative panel"
              onClick={() => setPanel(null)}
            >
              ×
            </button>
          </div>
          <div className="lt-composer-tabs">
            {['layouts', 'templates', 'assistant'].map((tab) => (
              <button
                key={tab}
                aria-pressed={panel === tab}
                onClick={() => setPanel(tab)}
              >
                {tab === 'assistant'
                  ? '✧ AI'
                  : tab === 'templates'
                  ? 'Saved'
                  : 'Layouts'}
              </button>
            ))}
          </div>
          {panel === 'layouts' && (
            <>
              <p className="lt-composer-caption">
                Add a section. Make it yours.
              </p>
              {studioBlocks.map((layout) => (
                <button
                  className="lt-layout"
                  key={layout.name}
                  disabled={busy}
                  onClick={() => {
                    if (editor.current && mode === 'write')
                      editor.current.appendTemplate(layout.html);
                    else change(value + layout.html);
                    setStartedWriting(true);
                  }}
                >
                  <span>↳</span>
                  <div
                    className="lt-layout-mini"
                    aria-hidden="true"
                    dangerouslySetInnerHTML={{ __html: layout.html }}
                  />
                  <strong>{layout.name}</strong>
                  <small>{layout.detail}</small>
                </button>
              ))}
              <label className="lt-composer-upload">
                ＋ Add media
                <input
                  type="file"
                  disabled={busy}
                  onChange={attach}
                  accept="image/*,video/*,audio/*,application/pdf"
                />
              </label>
              <p className="lt-composer-caption">
                Images, video, audio, PDFs. Inserted into this layout—not a
                separate story.
              </p>
            </>
          )}
          {panel === 'media' && (
            <div className="lt-media-insert">
              <div className="lt-media-art" aria-hidden="true">
                ▧<span>♫</span>▷
              </div>
              <h3>More than words.</h3>
              <p>
                Photos, film, a familiar voice. Add them right where your story
                needs them.
              </p>
              <label className="lt-composer-upload">
                ＋ Choose a file
                <input
                  type="file"
                  disabled={busy}
                  onChange={attach}
                  accept="image/*,video/*,audio/*,application/pdf"
                />
              </label>
              <small>
                Images, video, audio & PDFs. Select a spot in the canvas first,
                then choose your file.
              </small>
            </div>
          )}
          {panel === 'templates' && (
            <Dialog
              open
              onClose={() => setPanel(null)}
              maxWidth="lg"
              fullWidth
              aria-labelledby="lt-template-title"
              PaperProps={{ className: 'lt-template-dialog' }}
            >
              <DialogTitle id="lt-template-title">
                <span>Find your starting point.</span>
                <button
                  type="button"
                  aria-label="Close template library"
                  onClick={() => setPanel(null)}
                >
                  ×
                </button>
              </DialogTitle>
              <DialogContent>
                <p className="lt-composer-caption">
                  Choose a starting point. Make it yours.
                </p>
                <select
                  className="lt-template-collection"
                  aria-label="Template collection"
                  value={templateCollection}
                  onChange={(event) => {
                    setTemplateCollection(event.target.value);
                    setTemplateSearch('');
                  }}
                >
                  <option value="studio">Studio collection · 20 designs</option>
                  <option value="classic">Classic collection</option>
                </select>
                <input
                  className="lt-template-search"
                  aria-label="Search templates"
                  placeholder="Find a template…"
                  value={templateSearch}
                  onChange={(event) => setTemplateSearch(event.target.value)}
                />
                {templateCollection === 'studio' && (
                  <div
                    className="lt-template-categories"
                    role="group"
                    aria-label="Template categories"
                  >
                    {[
                      'All',
                      ...new Set(
                        studioTemplates.map((template) => template.category)
                      ),
                    ].map((category) => (
                      <button
                        key={category}
                        type="button"
                        aria-pressed={templateCategory === category}
                        onClick={() => setTemplateCategory(category)}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                )}
                {templatesLoading && <p role="status">Opening the library…</p>}
                {templatesError && (
                  <div role="alert">
                    <p>The library couldn’t load.</p>
                    <button
                      type="button"
                      onClick={() => setTemplateRetry((value) => value + 1)}
                    >
                      Try again
                    </button>
                  </div>
                )}
                <div className="lt-template-grid">
                  {visibleTemplates.map((template, i) => (
                    <button
                      className="lt-layout"
                      key={template.id || i}
                      disabled={!template.html}
                      aria-label={`Insert ${
                        template.name || template.title || `Template ${i + 1}`
                      }`}
                      onClick={() => {
                        if (editor.current && mode === 'write')
                          editor.current.appendTemplate(template.html);
                        else change(value + template.html);
                        setStartedWriting(true);
                        setPanel(null);
                      }}
                    >
                      <span className="lt-layout-mini" aria-hidden="true">
                        <span
                          dangerouslySetInnerHTML={{
                            __html: clean(
                              String(template.html || '').replace(/^"|"$/g, '')
                            ),
                          }}
                        />
                      </span>
                      <strong>
                        {template.name || template.title || `Template ${i + 1}`}
                      </strong>
                      <small>
                        {template.description || 'Add to your canvas ↗'}
                      </small>
                    </button>
                  ))}
                </div>
                {!templatesLoading && !templatesError && !templates.length && (
                  <p>No templates available yet.</p>
                )}
                {!templatesLoading &&
                  templates.length > 0 &&
                  !visibleTemplates.length && (
                    <p>No matching templates. Try another word or category.</p>
                  )}
              </DialogContent>
            </Dialog>
          )}
          {panel === 'assistant' && (
            <>
              <StudioAssistant
                key={index}
                value={value}
                title={title}
                onBusy={setBusy}
                getSelection={() => editor.current?.getSelection()}
                onApply={(html, selection, append) => {
                  if (selection && !editor.current) {
                    setError(
                      'Return to Compose before replacing selected words. Your content is unchanged.'
                    );
                    return false;
                  }
                  setPrevious(value);
                  if (selection && editor.current)
                    editor.current.replaceSelection(selection, html);
                  else change(append ? value + html : html);
                  setStartedWriting(true);
                  setMode('write');
                }}
              />
            </>
          )}
        </aside>
        <div className="lt-canvas-surround">
          <div className="lt-page-caption">
            <span>{mode === 'preview' ? 'READER VIEW' : 'YOUR CANVAS'}</span>
            <span>
              {width === 'phone' ? 'Mobile' : 'Responsive web'} ·{' '}
              {busy ? 'Working…' : 'Draft'}
            </span>
          </div>
          <div className={`lt-canvas lt-canvas-${width}`}>
            {mode === 'write' &&
              !startedWriting &&
              !value.replace(/<[^>]*>|&nbsp;/g, '').trim() &&
              !/<(img|iframe|video|audio)\b/i.test(value) && (
                <div className="lt-blank-start">
                  <span>IT STARTS WITH A LITTLE SOMETHING</span>
                  <h3>
                    Leave a little
                    <br />
                    <em>of yourself here.</em>
                  </h3>
                  <p>Write a note. Tell a story. Make someone’s day.</p>
                  <div>
                    <button
                      onClick={() => {
                        setStartedWriting(true);
                        document
                          .querySelector(
                            '[aria-label="LifeObject visual content"]'
                          )
                          ?.focus();
                      }}
                    >
                      Start writing ↗
                    </button>
                    <button onClick={() => setPanel('layouts')}>
                      Explore layouts
                    </button>
                    <button onClick={() => setPanel('assistant')}>
                      ✧ Imagine with AI
                    </button>
                  </div>
                </div>
              )}
            {mode === 'write' && (
              <TiptapCanvas
                key={index}
                value={value}
                editorDocument={content[index]?.editorDocument}
                onChange={change}
                onReady={onEditorReady}
                onUploadImage={upload}
                onBusy={setBusy}
              />
            )}
            {mode === 'preview' && (
              <div
                className="lt-rich-preview"
                dangerouslySetInnerHTML={{ __html: clean(value) }}
              />
            )}
            {mode === 'html' && (
              <>
                <textarea
                  className="lt-source"
                  aria-label="HTML source"
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                />
                <button
                  onClick={() => {
                    change(clean(source));
                    setMode('write');
                  }}
                >
                  Apply HTML
                </button>
                <p className="lt-composer-caption">
                  Executable scripts, forms, and unsafe links are removed when
                  applied.
                </p>
              </>
            )}
          </div>
          <footer className="lt-canvas-footer">
            {busy ? 'Working…' : 'Changes stay in this draft until you save.'}
            <span>HTML layout · Responsive media</span>
          </footer>
        </div>
      </div>
    </div>
  );
}
